import { Stack, Typography, useTheme } from "@mui/material";
import {
	CircleCheck,
	Clock,
	type LucideIcon,
	RotateCcw,
	SquarePen,
	Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useReminderCancelSnoozeMutation } from "web_myplann/Api/Reminders/useReminderCancelSnoozeMutation";
import { useReminderResolveMutation } from "web_myplann/Api/Reminders/useReminderResolveMutation";
import { ReminderStatusEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type {
	Reminder,
	ResourceKey,
} from "web_myplann/Reminders/Types/ReminderTypes";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { parseApiDate } from "web_myplann/Shared/Utils/apiDate";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";

type Props = {
	reminder: Reminder;
	open: boolean;
	onEdit: () => void;
	onSnooze: () => void;
	onDelete: () => void;
	onClose: () => void;
};

type ActionRow = {
	id: string;
	labelKey: ResourceKey;
	icon: LucideIcon;
	onClick: () => void;
};

export const ReminderActionsDrawer = ({
	reminder,
	open,
	onEdit,
	onSnooze,
	onDelete,
	onClose,
}: Props) => {
	const { t } = useResource();
	const theme = useTheme();

	const { mutate: resolveReminder } = useReminderResolveMutation();
	const { mutate: cancelSnooze } = useReminderCancelSnoozeMutation();

	const isSnoozed = reminder.status === ReminderStatusEnum.Snoozed;

	const handleResolve = () => {
		resolveReminder(reminder.id, {
			onSuccess: ({ nextOccurrence }) => {
				if (nextOccurrence) {
					toast.success(
						t(Resources.Reminders.Resolve.nextScheduledToast, {
							name: nextOccurrence.title,
							date: dateFormatter(parseApiDate(nextOccurrence.date)),
						}),
					);
				}
				onClose();
			},
		});
	};

	const handleCancelSnooze = () => {
		cancelSnooze(reminder.id, { onSuccess: onClose });
	};

	const stateAction: ActionRow = isSnoozed
		? {
				id: "cancelSnooze",
				labelKey: Resources.Reminders.Actions.cancelSnooze,
				icon: RotateCcw,
				onClick: handleCancelSnooze,
			}
		: {
				id: "snooze",
				labelKey: Resources.Reminders.Actions.snooze,
				icon: Clock,
				onClick: onSnooze,
			};

	const actions: ActionRow[] = [
		{
			id: "resolve",
			labelKey: Resources.Reminders.Actions.resolve,
			icon: CircleCheck,
			onClick: handleResolve,
		},
		stateAction,
		{
			id: "edit",
			labelKey: Resources.Reminders.Actions.edit,
			icon: SquarePen,
			onClick: onEdit,
		},
		{
			id: "delete",
			labelKey: Resources.Reminders.Actions.remove,
			icon: Trash2,
			onClick: onDelete,
		},
	];

	return (
		<MyPlannDrawer
			open={open}
			onClose={onClose}
			closeButton
			title={reminder.name}
		>
			<Stack px={2} pb={2} gap={1.2}>
				{actions.map((action) => {
					const Icon = action.icon;
					return (
						<Stack
							key={action.id}
							direction="row"
							alignItems="center"
							gap={2}
							onClick={action.onClick}
							sx={{ cursor: "pointer" }}
						>
							<Icon color={theme.palette.primary.main} />
							<Typography variant="body1">{t(action.labelKey)}</Typography>
						</Stack>
					);
				})}
			</Stack>
		</MyPlannDrawer>
	);
};
